import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import i18n from '@/i18n';

import { fetchUserProfile } from './auth-profile';
import { supabase } from '../integrations/supabase/client';
import { resetAuthCircuit } from '../lib/auth-circuit';
import { setSentryUser } from '../lib/sentry-user';
import { emitLogoutTeardown } from '../lib/logout-teardown-signal';
import { toast } from '../hooks/use-toast';
import { prefetchCapabilityMap, setSessionTokenGetter } from '../hooks/use-provider-capability';
import { clearAllAppData, getInitialBrowserAuthSession } from '../lib/mobile/auth-persistence';
import { startAuthSubscriptionBootstrap } from '../lib/mobile/auth-subscription-bootstrap';
import {
  didAuthIdentityChange,
  didAuthSessionEnd,
  isRedundantSessionRepublish,
  shouldRetainOfflineBootstrap,
} from '../lib/mobile/native-auth-bootstrap';
import { wipeOfflineDb } from '../lib/mobile/offline-db/connection';

import type { Session, User } from '@supabase/supabase-js';
import type { ReactNode } from 'react';
import type { UserProfile } from './auth-profile';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  /** True while React is running from a locally verified encrypted bootstrap. */
  isBootstrapSession: boolean;
  signOut: (options?: { scope?: 'local' | 'global' }) => Promise<{ error: Error | null }>;
  // Profile data
  profile: UserProfile | null;
  profileLoading: boolean;
  profileError: Error | null;
  // Role helpers
  isUser: boolean;
  isAdmin: boolean;
  isStaff: boolean;
  isAssistant: boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  setSuppressSessionToast: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function clearTerminatedAuthData(): Promise<void> {
  await clearAllAppData().catch(() => null);
}

type IdentityClearMode = 'replace' | 'session-loss' | 'silent-session-loss';

async function clearIdentityData(client: QueryClient, mode: IdentityClearMode): Promise<void> {
  await client.cancelQueries().catch(() => null);
  await (mode === 'replace' ? wipeOfflineDb().catch(() => null) : clearTerminatedAuthData());
  emitLogoutTeardown();
  client.clear();
  localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE');
  if (mode === 'session-loss') {
    toast({
      title: i18n.t('auth:session.expiredTitle'),
      description: i18n.t('auth:session.expiredBody'),
    });
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }: Readonly<{ children: ReactNode }>) => {
  // Browser reloads can seed from localStorage. Native waits for the Supabase
  // client because its source of truth is the async SecureStorage adapter.
  const [session, setSession] = useState<Session | null>(getInitialBrowserAuthSession);
  const [user, setUser] = useState<User | null>(() => session?.user ?? null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isBootstrapSession, setIsBootstrapSession] = useState(false);
  const queryClient = useQueryClient();
  // Track whether signout was user-initiated to avoid showing "session expired" toast
  const wasUserInitiatedSignOutRef = useRef(false);
  // Allow pages with their own recovery UX (e.g., /welcome) to suppress the global toast
  const suppressSessionToastRef = useRef(false);
  // Mirrors hadUserBeforeRef in organization-context.tsx. Distinguishes a real
  // session loss (TOKEN_REFRESHED with null on a revoked refresh token, etc.)
  // from a first-time visitor's empty boot. Explicit SIGNED_OUT events bypass
  // this latch entirely — they're always a real signal worth acting on.
  const hadSessionBeforeRef = useRef(false);
  const bootstrapSessionRef = useRef<Session | null>(null);
  const currentSessionRef = useRef<Session | null>(session);
  // What this handler last pushed into React state. Distinct from
  // `currentSessionRef`, which also holds sessions adopted outside the handler
  // (localStorage hydration, the native encrypted bootstrap) and so cannot tell
  // "already published" from "never applied".
  const lastPublishedSessionRef = useRef<Session | null>(null);
  // Incrementing counter stamped on every SIGNED_IN/SIGNED_OUT boundary.
  // Passed to `prefetchCapabilityMap` so a mid-flight prefetch can detect a
  // session change before its response lands in cache. Without this guard,
  // Supabase SDK v2's lack of AbortSignal support means a slow prefetch
  // can write User A's capability map into User B's freshly-signed-in cache.
  const sessionTokenRef = useRef(0);
  const getSessionToken = useCallback(() => sessionTokenRef.current, []);
  const setSuppressSessionToast = useCallback((value: boolean) => {
    suppressSessionToastRef.current = value;
  }, []);

  // Profile query using TanStack Query
  const {
    data: profile,
    isLoading: profileLoading,
    error: profileError,
  } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => await fetchUserProfile(user.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes,
    refetchOnMount: true,
  });

  // Role helper functions
  const isUser = profile?.role === 'user';
  const isAdmin = profile?.role === 'admin';
  const isStaff = profile?.role === 'staff';
  const isAssistant = profile?.role === 'assistant';

  // Reading `profile?.role` once locks the callback identity to that exact
  // value (matches the body's read). Using `[profile?.role]` as the dep
  // trips React Compiler's preserve-manual-memoization rule.
  const role = profile?.role;
  const hasRole = useCallback((target: string) => role === target, [role]);
  const hasAnyRole = useCallback((list: string[]) => Boolean(role && list.includes(role)), [role]);

  useEffect(() => {
    let isMounted = true;

    // Register the token getter so BOTH the prefetch path AND
    // `useQuery(capabilityMapQueryOptions)`'s refetch (mount / window-focus)
    // consult the same live token. The default getter (`() => 0`) is safe
    // in the brief window before this effect runs.
    setSessionTokenGetter(getSessionToken);

    // Prime the session-loss latch from the hydrated session so a cross-tab
    // SIGNED_OUT broadcast — or TOKEN_REFRESHED-with-null on a revoked
    // refresh token — can be distinguished from a first-time visitor.
    if (session) {
      hadSessionBeforeRef.current = true;
    }

    // Loading is owned by onAuthStateChange's INITIAL_SESSION event, not by
    // getSession()'s resolution. Per Supabase v2 docs, INITIAL_SESSION fires
    // exactly once after the SDK finishes parsing both storage AND the URL
    // hash (with detectSessionInUrl). A separate getSession() call races
    // that parse: on /auth/callback#access_token=…, getSession() can resolve
    // with null before the SDK has consumed the hash, flipping loading=false
    // with user=null and triggering a redirect to /auth/login moments before
    // SIGNED_IN arrives.

    const applyAuthStateChange = async (
      event: string,
      nextSession: Session | null,
      previousUserIdOverride?: string | null,
      forceIdentityReplacement = false,
    ) => {
      if (!isMounted) {
        return;
      }

      // auth-js reports INITIAL_SESSION(null) when an expired stored token
      // cannot refresh while offline. The Keychain session plus the verified
      // encrypted bootstrap remains the local authority until reconnect; a
      // real SIGNED_OUT event still tears it down below.
      if (await shouldRetainOfflineBootstrap(event, nextSession, bootstrapSessionRef.current)) {
        setLoading(false);
        return;
      }

      // A replacement session can arrive without an intervening SIGNED_OUT
      // event (for example, an OAuth account switch). Retire the previous
      // identity while its authenticated tree is still mounted, and never run
      // clearAllAppData here because auth-js may already have persisted the new
      // session's credentials.
      const changedAccount = didAuthIdentityChange(
        nextSession,
        currentSessionRef.current,
        previousUserIdOverride,
        forceIdentityReplacement,
      );
      // A window refocus re-notifies the session already in state. Publishing
      // that copy swaps the identity consumers key on — the route guard's
      // access check among them — which tears down and rebuilds the
      // authenticated tree, discarding unsaved local UI state such as an open
      // composer. Identical credentials cannot yield a different authorization
      // decision, so the republish is dropped; every path that must still run
      // (teardown, account switch, refreshed token, and the first event for a
      // session this handler has not published yet) fails the check.
      if (
        isRedundantSessionRepublish(
          event,
          nextSession,
          lastPublishedSessionRef.current,
          changedAccount,
        )
      ) {
        setLoading(false);
        return;
      }

      if (changedAccount) {
        sessionTokenRef.current += 1;
        await clearIdentityData(queryClient, 'replace');
        bootstrapSessionRef.current = null;
      }

      // SIGNED_OUT always counts. INITIAL_SESSION never counts (preserves
      // rehydrated persist cache) unless native already admitted an encrypted
      // bootstrap. Other null events count only if we saw a session this
      // lifetime (filters SDK noise on stale/empty tokens).
      const sessionLost = didAuthSessionEnd(
        event,
        nextSession,
        hadSessionBeforeRef.current,
        bootstrapSessionRef.current !== null,
      );

      // Retire auth storage, encrypted offline data, and all query state before
      // publishing null. The teardown signal must reach shell listeners while
      // the authenticated tree is still mounted.
      if (sessionLost) {
        sessionTokenRef.current += 1;
        const mode =
          wasUserInitiatedSignOutRef.current || suppressSessionToastRef.current
            ? 'silent-session-loss'
            : 'session-loss';
        await clearIdentityData(queryClient, mode);
      }

      currentSessionRef.current = nextSession;
      lastPublishedSessionRef.current = nextSession;
      setSession(nextSession);
      setUser(nextSession?.user || null);
      setSentryUser(nextSession?.user?.id ?? null);
      setIsBootstrapSession(false);
      if (nextSession || sessionLost) {
        bootstrapSessionRef.current = null;
      }
      if (nextSession || event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
        setLoading(false);
      }
      if (nextSession) {
        hadSessionBeforeRef.current = true;
      }

      // Re-arm the auth circuit breaker when a fresh session is established.
      // Reset the user-initiated-signout latch too: a re-signin (e.g., OAuth
      // popup without a full reload) must allow future expiry toasts to fire.
      if (event === 'SIGNED_IN' && nextSession) {
        wasUserInitiatedSignOutRef.current = false;
        resetAuthCircuit();
        // Stamp this session before firing the prefetch — the prefetch
        // closure captures this token at start and re-checks it on
        // completion. A mid-flight signout/signin will bump the token
        // and the prefetch will refuse to write stale capabilities.
        sessionTokenRef.current += 1;
        // Warm the correspondence-api capability map so destructive UI
        // (Send button, Archive, Delete) has a real answer to gate on
        // by first render — rather than the 30-second conservative-default
        // window the hook falls back to during cold-cache auth.
        prefetchCapabilityMap(queryClient, getSessionToken);
      }
    };

    const stopAuthSubscription = startAuthSubscriptionBootstrap({
      supabase,
      queryClient,
      applyAuthStateChange,
      onBootstrapSession: (storedSession) => {
        bootstrapSessionRef.current = storedSession;
        currentSessionRef.current = storedSession;
        hadSessionBeforeRef.current = true;
        setSession(storedSession);
        setUser(storedSession.user);
        setSentryUser(storedSession.user.id);
        setIsBootstrapSession(true);
        setLoading(false);
      },
      onSuppressedInitialSession: () => setLoading(false),
      onInitializationError: () => {
        console.error('[AuthPersistence] Failed to initialize native auth hydration');
      },
    });

    return () => {
      isMounted = false;
      stopAuthSubscription();
    };
    // `session` is only read here to set the latch on first mount; including
    // it as a dep would re-subscribe to onAuthStateChange on every session
    // change (the listener already handles those internally).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, getSessionToken]);

  const signOut = useCallback(
    async (options?: { scope?: 'local' | 'global' }) => {
      // Scope is deliberate (bare signOut() defaults to GLOBAL - it revokes
      // every session on every device, which is how signing out of the web app
      // used to kill the phone). 'local' revokes this session only; 'global' is
      // reserved for the explicit sign-out-everywhere surface. An offline local
      // sign-out still dies server-side within one JWT lifetime: the teardown
      // below destroys the refresh token, so nothing can ever renew the session.
      const scope = options?.scope ?? 'local';
      wasUserInitiatedSignOutRef.current = true;
      try {
        const { data: factorsData } = await supabase.auth.mfa.listFactors();
        const unverifiedFactors = factorsData?.totp?.filter((f) => f.status === 'unverified') || [];

        if (unverifiedFactors.length > 0) {
          for (const factor of unverifiedFactors) {
            await supabase.auth.mfa.unenroll({ factorId: factor.id });
          }
        }
      } catch (error) {
        // Auth errors can echo account context in their message — log the error
        // name only (Sentry captures console.error).
        console.error(
          'Error cleaning up MFA factors on signout:',
          error instanceof Error ? error.name : 'unknown',
        );
      }

      // A transport failure REJECTS rather than resolving to `{ error }`. R17
      // draws no distinction between the two: an unreachable auth server must
      // still destroy the local session, so the rejection is captured and
      // reported through the normal return value instead of unwinding the
      // teardown below.
      let error: Error | null = null;
      try {
        ({ error } = await supabase.auth.signOut({ scope }));
      } catch (error_) {
        error = error_ instanceof Error ? error_ : new Error('Sign-out failed');
      }

      // Sole owner of auth-storage teardown, across every shell: web keeps the
      // session in localStorage, desktop behind the bridge's authStorage, native
      // in the Keychain. Clearing one by hand here would silently miss the others
      // — and without this, a failed remote signOut() leaves a session that a cold
      // restart recovers. Runs regardless of `error` for exactly that case, and
      // swallows its own failure so a wedged keychain cannot abort the rest of
      // sign-out (it logs internally, without PII).
      await clearTerminatedAuthData();

      // Unconditional emit AFTER the awaits, BEFORE the unmount-driving setState:
      // on a non-ignored signOut() error no SIGNED_OUT event fires, yet we still
      // clear user/session below — without this the shell would take the
      // authenticated 'close' path post-signout. Emitting after the awaits (not
      // at function top) keeps the registries armed during the multi-second
      // MFA/sign-out window, so resources created mid-window still get torn down.
      // Idempotent downstream via each provider's firedRef.
      emitLogoutTeardown();

      // Always clear user state, even if there's an error
      // (e.g., session already expired/deleted)
      setUser(null);
      setSentryUser(null);
      setSession(null);
      currentSessionRef.current = null;
      setIsBootstrapSession(false);
      bootstrapSessionRef.current = null;

      // Clear all React Query cache to prevent stale data
      queryClient.clear();
      localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE');

      // The Supabase auth keys are NOT removed here. `clearAllAppData` above owns
      // them on every platform and clears all three (token, `-code-verifier`,
      // `-user`); a second removeItem of the token key alone used to leave the
      // other two on disk.

      return { error };
    },
    [queryClient],
  );

  const value: AuthContextType = useMemo(
    () => ({
      user,
      session,
      loading,
      isBootstrapSession,
      signOut,
      profile,
      profileLoading,
      profileError,
      isUser,
      isAdmin,
      isStaff,
      isAssistant,
      hasRole,
      hasAnyRole,
      setSuppressSessionToast,
    }),
    [
      user,
      session,
      loading,
      isBootstrapSession,
      signOut,
      profile,
      profileLoading,
      profileError,
      isUser,
      isAdmin,
      isStaff,
      isAssistant,
      hasRole,
      hasAnyRole,
      setSuppressSessionToast,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
